import React, { FunctionComponent, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { ModalBaseProps } from 'react-native'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { CloseIcon } from '../../atoms/icons/CloseIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { TertiaryButton } from '../../atoms/tertiaryButton/TertiaryButton'
import { Title } from '../../atoms/title/Title'
import { useMediaQuerySize } from '../../hooks/useMediaQuerySize'
import { Modal } from '../../organisms/modal/Modal'
import { BodyTextSize, TextAlign, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface GenericModalWrapperProps {
  /** Controls modal visibility */
  isOpen: boolean
  /** Called by the close (X) icon and the background scrim */
  onClose: () => void
  /** Already-localized title. A plain string, or a rich node (e.g. `FormattedMessage` with bold chunks). Optional — omit for a bare shell (e.g. when `content` supplies its own header). */
  title?: React.ReactNode
  /** Already-localized secondary heading rendered directly under the title */
  subhead?: string
  /** The middle content slot — the only place arbitrary content is rendered. Alignment is the caller's responsibility. */
  content?: React.ReactNode
  /** When provided, the illustration is shown above the title/subhead. */
  illustrationElement?: React.ReactElement
  /** Alignment of the title/subhead. Defaults to `'center'`. */
  textAlignment?: TextAlign
  /** Primary action. When omitted, the primary button is not rendered. Does NOT auto-close — the caller controls closing. */
  primaryCTA?: () => void
  /** Secondary action. When omitted, the secondary button is not rendered. Does NOT auto-close — the caller controls closing. */
  secondaryCTA?: () => void
  /** Primary button label. Defaults to "Continue". */
  primaryButtonText?: React.ReactNode
  /** Secondary button label. Defaults to "Cancel". */
  secondaryButtonText?: React.ReactNode
  /** Override the primary button's testID. Defaults to `${testID}-primaryButton`. */
  primaryButtonTestID?: string
  /** Override the secondary button's testID. Defaults to `${testID}-secondaryButton`. */
  secondaryButtonTestID?: string
  /** Override the close (X) icon's testID. Defaults to `${testID}-closeIcon`. */
  closeIconTestID?: string
  snapPoints?: (string | number)[]
  /** Desktop pop-up width (passed to Modal). Defaults to Modal's `'544px'`. */
  width?: string
  /** Accessibility label for the modal. Falls back to `title` when it is a plain string. */
  accessibilityLabel?: string
  /** Native ID applied to the content container (maps to the DOM `id` on web). */
  nativeID?: string
  /** Prefix for child testIDs. */
  testID?: string
  animationType?: ModalBaseProps['animationType']
}

const MainContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.breakpoints.isMobileSized
    ? `${theme.spacing['12px']} ${theme.spacing['16px']} ${theme.spacing['8px']} ${theme.spacing['16px']}`
    : 0,
  justifyContent: 'space-between',
}))

const MainContentContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['32px'],
}))

const ButtonContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  alignSelf: 'flex-end',
  width: theme.breakpoints.isMobileSized ? '100%' : 'auto',
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['8px'] : 0,
  flexDirection: theme.breakpoints.isMobileSized ? 'column-reverse' : 'row',
  gap: theme.breakpoints.isMobileSized ? theme.spacing['8px'] : theme.spacing['16px'],
}))

const CloseIconContainer = styled(PressableOpacity)(({ theme }) => ({
  alignSelf: 'flex-end',
  position: 'absolute',
  padding: theme.breakpoints.isMobileSized ? '9px' : '5px',
  zIndex: 1,
}))

const IconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['8px'] : theme.spacing['16px'],
  marginLeft: 'auto',
  marginRight: 'auto',
}))

const TitleContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingTop: theme.breakpoints.isMobileSized ? '20px' : theme.spacing['16px'],
  marginBottom: theme.spacing['16px'],
}))

/**
 * Reusable modal frame (web, Aurora + Select). Renders an optional illustration, a title, an
 * optional subhead, an arbitrary middle `content` slot, and up to two action buttons, all on top
 * of `Modal`. Intended as the shared basis for generic modals (e.g. `TextModal`).
 */
export const GenericModalWrapper: FunctionComponent<GenericModalWrapperProps> = ({
  isOpen,
  onClose,
  title,
  subhead,
  content,
  illustrationElement,
  primaryCTA,
  secondaryCTA,
  primaryButtonText,
  secondaryButtonText,
  primaryButtonTestID,
  secondaryButtonTestID,
  closeIconTestID,
  snapPoints = [],
  width,
  accessibilityLabel,
  nativeID,
  textAlignment = 'left',
  testID = 'GenericModalWrapper',
  animationType,
}) => {
  const { isMobileSized } = useMediaQuerySize()
  const { colors } = useTheme() as ThemeType
  const { formatMessage } = useIntl()

  const bottomSheetRef = useRef<BottomSheetGorhom>(null)

  const openBottomSheet = () => {
    bottomSheetRef.current?.expand()
  }

  const closeModal = () => {
    onClose()
    bottomSheetRef.current?.close()
  }

  const customStyling = {
    borderRadius: 24,
  }

  const showButtons = Boolean(primaryCTA || secondaryCTA)
  const showTitleContainer = illustrationElement !== undefined || Boolean(title) || Boolean(subhead)

  return (
    <Modal
      modalContents={
        <MainContainer testID={tID(testID)} nativeID={nativeID}>
          <MainContentContainer>
            <CloseIconContainer
              testID={closeIconTestID ?? tID(`${testID}-closeIcon`)}
              onPress={closeModal}
              accessibilityLabel={formatMessage({
                defaultMessage: 'Close',
                description: 'Accessibility label for close button',
              })}
            >
              <CloseIcon size={14} fillColor={colors.iconDefault} />
            </CloseIconContainer>
            {showTitleContainer && (
              <TitleContainer>
                {illustrationElement && (
                  <IconContainer testID={tID(`${testID}-illustration`)}>{illustrationElement}</IconContainer>
                )}
                {Boolean(title) && (
                  <Title
                    size={TitleSize.MEDIUM}
                    text={title}
                    textAlign={textAlignment}
                    testID={tID(`${testID}-title`)}
                  />
                )}
                {Boolean(subhead) && (
                  <BodyText
                    size={BodyTextSize.LARGE}
                    text={subhead}
                    textAlign={textAlignment}
                    testID={tID(`${testID}-subhead`)}
                  />
                )}
              </TitleContainer>
            )}
            {content}
          </MainContentContainer>

          {showButtons && (
            <ButtonContainer>
              {secondaryCTA && (
                <TertiaryButton
                  onPress={secondaryCTA}
                  text={
                    secondaryButtonText ?? (
                      <FormattedMessage defaultMessage='Cancel' description='Generic modal secondary button text' />
                    )
                  }
                  testID={secondaryButtonTestID ?? tID(`${testID}-secondaryButton`)}
                  fullWidth={isMobileSized}
                  customTextColor={colors.textActive}
                />
              )}
              {primaryCTA && (
                <PrimaryButton
                  onPress={primaryCTA}
                  text={
                    primaryButtonText ?? (
                      <FormattedMessage defaultMessage='Continue' description='Generic modal primary button text' />
                    )
                  }
                  testID={primaryButtonTestID ?? tID(`${testID}-primaryButton`)}
                  fullWidth={isMobileSized}
                />
              )}
            </ButtonContainer>
          )}
        </MainContainer>
      }
      onRequestClose={closeModal}
      visible={isOpen}
      snapPoints={snapPoints}
      width={width}
      disableBottomSheet={!isMobileSized}
      onLayout={openBottomSheet}
      onCloseEnd={onClose}
      scrollable={isMobileSized}
      backgroundColor={colors.backgroundModal}
      useDynamicHeightForBottomSheet
      customBottomSheetStyle={customStyling}
      style={customStyling}
      accessibilityLabel={accessibilityLabel}
      animationType={animationType}
    />
  )
}
